import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { collection, getDoc, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import BASE_QUERY, { authBaseQuery } from '@/features/baseQuery';
import { dealsApi } from '../deals/dealsApi';
import { engagementsApi } from '../engagements/engagementsApi';
import { createDealAssignedNotification } from '../deals/helper';
import testClaimJson from '@/assets/json/test-claim.json';
import testDealJson from '@/assets/json/test-deal.json';

// Creating an API service using RTK Query
export const ticketsApi = createApi({
	reducerPath: 'ticketsApi',
	tagTypes: ['TICKETS', 'TICKET', 'TICKET_ROW'],
	baseQuery: authBaseQuery,
	endpoints: (builder) => ({
		// Defining an endpoint to get Insight claim for specific deal
		getInsightClaim: builder.query({
			providesTags: (result, error, dealId) => {
				return [{ type: 'TICKET', id: dealId }];
			},
			queryFn: async (dealId, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					if (dealId !== testDealJson?.id) {
						const res = await baseQuery({
							url: process.env.REACT_APP_RETRIEVE_INSIGHT_CLAIM,
							method: 'POST',
							body: { dealId },
						});
						console.log('getInsightClaim data', res?.data?.data?.Claim);
						return { data: res?.data?.data?.Claim };
					} else {
						return { data: testClaimJson.Claim };
					}
				} catch (error) {
					return { error: { status: 'CUSTOM_ERROR', error: error.message } };
				}
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getInsightClaim error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform getInsightClaim response', response);
				return response;
			},
		}),
		createClaim: builder.mutation({
			// This tag indicates that this mutation invalidates the list of clients
			invalidatesTags: (result, error, arg) => [{ type: 'TICKETS', id: 'LIST' }],
			query: ({
				dealName,
				physicalAddress,
				description,
				dateOfLoss,
				dateReported,
				notes,
				policyId,
				policyTransId,
				insurer,
				claimType,
				subjectOfLossDetails,
				clientId,
				completedBy,
				brokerGroupId,
				...params
			}) => ({
				url: `${process.env.REACT_APP_DEAL_API}/create/claim`,
				method: 'POST',
				body: {
					dealName,
					physicalAddress,
					description,
					dateOfLoss,
					dateReported,
					notes,
					policyId,
					policyTransId,
					insurer,
					claimType,
					subjectOfLossDetails,
					clientId,
					completedBy,
					brokerGroupId,
					...params,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform createClaim response', response);
				return response;
			},
			onQueryStarted: async ({ ownerDocId }, { dispatch, getState, queryFulfilled }) => {
				try {
					const res = await queryFulfilled;
					const dealId = res.data?.dealId;
					if (dealId) {
						await createDealAssignedNotification({
							dispatch,
							getState,
							dealId,
							assigneeDocId: ownerDocId,
						});
					}
					dispatch(dealsApi.util.invalidateTags(['CLIENT_DEALS']));
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		updateInsightClaim: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'TICKET', id: arg.dealId },
				{ type: 'TICKETS', id: 'LIST' },
			],
			query: ({ dealId, properties }) => ({
				url: process.env.REACT_APP_UPDATE_INSIGHT_CLAIM,
				method: 'POST',
				body: {
					dealId,
					properties,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform updateInsightClaim response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;

					dispatch(dealsApi.util.invalidateTags(['CLIENT_DEALS']));
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		retrieveClaimStatusList: builder.query({
			query: () => ({
				url: process.env.REACT_APP_GET_INSIGHT_CLAIMS_STATUS_LIST,
				method: 'POST',
				body: {},
			}),
			transformResponse: (response) => {
				console.log(
					':space_invader: ~ transform retrieveClaimStatusList response',
					response,
				);
				return response?.data?.Results ?? [];
			},
		}),
		retrieveClaimInsurers: builder.query({
			invalidatesTags: (result, error, arg) => [{ type: 'TICKETS', id: 'LIST' }],
			query: () => ({
				url: process.env.REACT_APP_RETRIEVE_INSURER_LIST,
				method: 'POST',
				body: {},
			}),
			transformResponse: (response) => {
				console.log(':space_invader: ~ transform retrieve Insurer list', response);
				return response;
			},
		}),
		getFirebaseClaims: builder.query({
			invalidatesTags: (result, error, arg) => [{ type: 'TICKETS', id: 'LIST' }],
			query: ({ brokerGroupId, tab, groupView }) => ({
				url: process.env.REACT_APP_RETRIEVE_FIREBASE_CLAIMS,
				method: 'POST',
				body: { brokerGroupId, tab, groupView },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform get firebase claims error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform get firebase claims response', response);
				return response;
			},
		}),
		retrieveFirebaseClaimStatus: builder.query({
			providesTags: (result, error, { ticketId }) => [{ type: 'TICKET', id: ticketId }],
			queryFn: async ({ ticketId, ledger }) => {
				try {
					const collectionRef = collection(firestore, 'insightToHubspotClaimStatus');

					const q = query(collectionRef, where('ledger', '==', ledger));
					const snapshot = await getDocs(q);

					const statusDocs = snapshot.docs.map((docSnap) => ({
						id: docSnap.id,
						...docSnap.data(),
					}));

					return { data: statusDocs };
				} catch (error) {
					return {
						error: { status: 'CUSTOM_ERROR', error: error.message },
					};
				}
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useGetInsightClaimQuery,
	useLazyGetInsightClaimQuery,
	useCreateClaimMutation,
	useUpdateInsightClaimMutation,
	useRetrieveClaimInsurersQuery,
	useRetrieveClaimStatusListQuery,
	useGetFirebaseClaimsQuery,
	useRetrieveFirebaseClaimStatusQuery,
} = ticketsApi;
